/**
 * GLTF meshes (especially KHR_mesh_quantization + meshopt) can produce vertex attributes
 * whose byte stride is not a multiple of four, or whose component layout has no WebGPU
 * GPUVertexFormat (e.g. snorm16x3). WebGL accepts these; WebGPU rejects them when building
 * render pipelines (including ShadowMaterial shadow-map passes).
 *
 * Deinterleave affected geometry, then promote misaligned / unsupported attributes to
 * Float32 (same denormalized values WebGL uses via BufferAttribute#getComponent).
 * Safe for WebGL and WebGPU.
 */

import * as THREE from 'three';
import { deinterleaveAttribute as deinterleaveAttributeImpl } from 'three/addons/utils/BufferGeometryUtils.js';

import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

const deinterleaveAttribute = deinterleaveAttributeImpl as unknown as (
  attribute: THREE.InterleavedBufferAttribute
) => THREE.BufferAttribute;

function isInterleavedBufferAttribute(
  attr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute | undefined
): attr is THREE.InterleavedBufferAttribute {
  return attr !== undefined && 'isInterleavedBufferAttribute' in attr && attr.isInterleavedBufferAttribute === true;
}

function deinterleaveGeometryAttributes(geometry: THREE.BufferGeometry): void {
  const attrMap = new Map<THREE.InterleavedBufferAttribute, THREE.BufferAttribute>();
  const attributes = geometry.attributes;

  for (const key of Object.keys(attributes)) {
    const attr = attributes[key as keyof typeof attributes];
    if (!isInterleavedBufferAttribute(attr)) {
      continue;
    }
    const interleaved = attr;
    if (!attrMap.has(interleaved)) {
      attrMap.set(interleaved, deinterleaveAttribute(interleaved));
    }
    (attributes as Record<string, THREE.BufferAttribute | THREE.InterleavedBufferAttribute>)[key] = attrMap.get(
      interleaved
    )!;
  }

  const morph = geometry.morphAttributes as Record<
    string,
    Array<THREE.BufferAttribute | THREE.InterleavedBufferAttribute> | undefined
  >;
  for (const morphName of Object.keys(morph)) {
    const morphArray = morph[morphName];
    if (!Array.isArray(morphArray)) {
      continue;
    }
    for (let i = 0; i < morphArray.length; i++) {
      const attr = morphArray[i];
      if (!isInterleavedBufferAttribute(attr)) {
        continue;
      }
      const interleaved = attr;
      if (!attrMap.has(interleaved)) {
        attrMap.set(interleaved, deinterleaveAttribute(interleaved));
      }
      morphArray[i] = attrMap.get(interleaved)!;
    }
  }
}

function getAttributeByteStride(attribute: THREE.BufferAttribute): number {
  return attribute.itemSize * attribute.array.BYTES_PER_ELEMENT;
}

/**
 * Returns true when the attribute cannot be bound as-is on WebGPU.
 */
export function needsWebGpuVertexLayoutFix(attribute: THREE.BufferAttribute): boolean {
  if (attribute instanceof THREE.Float32BufferAttribute && !attribute.normalized) {
    return false;
  }

  const bytesPerElement = attribute.array.BYTES_PER_ELEMENT;
  const byteStride = getAttributeByteStride(attribute);

  if (byteStride % 4 !== 0) {
    return true;
  }

  // Normalized 32-bit integers map to invalid WebGPU vertex formats (unorm32x*, snorm32x*).
  if (attribute.normalized && (attribute.array instanceof Int32Array || attribute.array instanceof Uint32Array)) {
    return true;
  }

  // WebGPU has no 8-bit or 16-bit vec3/scalar vertex formats — only vec2/vec4 at those widths.
  if (bytesPerElement < 4 && (attribute.itemSize === 3 || attribute.itemSize === 1)) {
    return true;
  }

  return false;
}

export function convertAttributeToFloat32(attribute: THREE.BufferAttribute): THREE.Float32BufferAttribute {
  const { count, itemSize } = attribute;
  const floatArray = new Float32Array(count * itemSize);
  for (let i = 0; i < count; i++) {
    for (let j = 0; j < itemSize; j++) {
      floatArray[i * itemSize + j] = attribute.getComponent(i, j);
    }
  }
  return new THREE.Float32BufferAttribute(floatArray, itemSize, false);
}

function sanitizeBufferAttribute(attribute: THREE.BufferAttribute): THREE.BufferAttribute {
  if (!needsWebGpuVertexLayoutFix(attribute)) {
    return attribute;
  }
  const converted = convertAttributeToFloat32(attribute);
  attribute.dispose();
  return converted;
}

function sanitizeBufferGeometryAttributes(geometry: THREE.BufferGeometry): void {
  for (const name of Object.keys(geometry.attributes)) {
    const attribute = geometry.attributes[name];
    if (!(attribute instanceof THREE.BufferAttribute)) {
      continue;
    }
    const sanitized = sanitizeBufferAttribute(attribute);
    if (sanitized !== attribute) {
      geometry.setAttribute(name, sanitized);
    }
  }

  const morph = geometry.morphAttributes as Record<
    string,
    Array<THREE.BufferAttribute | THREE.InterleavedBufferAttribute> | undefined
  >;
  for (const morphName of Object.keys(morph)) {
    const morphArray = morph[morphName];
    if (!Array.isArray(morphArray)) {
      continue;
    }
    for (let i = 0; i < morphArray.length; i++) {
      const attribute = morphArray[i];
      if (!(attribute instanceof THREE.BufferAttribute)) {
        continue;
      }
      morphArray[i] = sanitizeBufferAttribute(attribute);
    }
  }
}

function sanitizeBufferGeometryForGpu(geometry: THREE.BufferGeometry): void {
  deinterleaveGeometryAttributes(geometry);
  sanitizeBufferGeometryAttributes(geometry);
}

/**
 * Run after GLTFLoader.parse: fixes geometry for WebGPU (and keeps behavior consistent on WebGL).
 * Uses a geometry WeakSet so shared BufferGeometry is only processed once.
 */
export function sanitizeGltfLoadedModel(gltf: GLTF): void {
  const seen = new WeakSet<THREE.BufferGeometry>();
  const visitGeometry = (geometry: THREE.BufferGeometry): void => {
    if (seen.has(geometry)) {
      return;
    }
    seen.add(geometry);
    sanitizeBufferGeometryForGpu(geometry);
  };

  const roots = gltf.scenes?.length ? gltf.scenes : [gltf.scene];
  for (const root of roots) {
    if (!root) {
      continue;
    }
    root.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj.geometry) {
        visitGeometry(obj.geometry);
      }
    });
  }
}
